import { useEffect, useRef, useState } from "react";
import { Loader2, MessageCircle, Send, Sun, X } from "lucide-react";
import { API } from "@/lib/api";
import { getLang } from "@/lib/i18n";

const getSessionId = () => {
  let id = localStorage.getItem("apollo_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("apollo_chat_session", id);
  }
  return id;
};

const GREETING = { id: "greeting", role: "assistant", content: "Ciao, sono Apollo! Posso aiutarti a capire se il fotovoltaico fa per te. Cosa vuoi sapere?" };
const msgId = () => crypto.randomUUID();

// Chiamata AI in streaming (SSE) verso il backend, che inoltra a Gemini
async function streamChat(message, onDelta) {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: getSessionId(), message, lang: getLang() }),
  });
  if (!res.ok) throw new Error("chat");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const ev = JSON.parse(data);
      if (ev.error) throw new Error(ev.error);
      onDelta(ev.delta);
    }
  }
}

// Markdown minimale: **grassetto** e liste puntate
const renderText = (text) =>
  text.replace(/^\s*[*-]\s+/gm, "• ").split("**").map((part, i) => (i % 2 ? <strong key={`b${i}-${part.slice(0, 12)}`}>{part}</strong> : part));

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div translate={i === 0 ? undefined : "no"} data-testid={`chat-message-${m.role}-${i}`}
      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md bg-slate-900 text-white" : "rounded-bl-md bg-slate-100 text-slate-800"}`}>
      {m.content ? renderText(m.content) : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { id: msgId(), role: "user", content: text }, { id: msgId(), role: "assistant", content: "" }]);
    const patchLast = (fn) => setMessages((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: fn(m[m.length - 1].content) }]);
    try {
      await streamChat(text, (delta) => patchLast((c) => c + delta));
    } catch (err) {
      patchLast(() => (err.message && err.message !== "chat" ? err.message : "Al momento non riesco a rispondere. Riprova tra poco o scrivici su WhatsApp."));
    }
    setLoading(false);
  };

  return (
    <>
      {open && (
        <div data-testid="chat-window" className="fade-up fixed bottom-24 right-4 z-[60] flex h-[min(34rem,calc(100vh-8rem))] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:right-6 sm:w-96">
          <div className="flex items-center justify-between bg-[#0B0F17] px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-slate-900"><Sun className="h-5 w-5" /></span>
              <div>
                <p className="font-display font-semibold">Apollo</p>
                <p className="text-xs text-slate-400">Consulente solare AI</p>
              </div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Chiudi chat" className="rounded-full p-1.5 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
          <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((m, i) => <Bubble key={m.id} m={m} i={i} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="flex gap-2 border-t border-slate-200 p-3">
            <input data-testid="chat-input-field" value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000}
              placeholder="Scrivi una domanda…" className="h-11 flex-1 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-emerald-500" />
            <button data-testid="chat-send-button" disabled={loading || !input.trim()} aria-label="Invia"
              className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500 text-slate-900 transition-colors hover:bg-emerald-400 disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
      <button data-testid="floating-chat-toggle-button" onClick={() => setOpen((o) => !o)} aria-label="Apri chat Apollo"
        className="fixed bottom-5 right-4 z-[60] grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-slate-900 shadow-xl shadow-emerald-500/30 transition-transform hover:scale-105 sm:right-6">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
};
